import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

type NotificationRow = {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string | null;
  read_at: string | null;
  created_at: string;
};

function useRelativeTime() {
  const { t, i18n } = useTranslation();
  return (iso: string) => {
    const diffMs = Date.now() - new Date(iso).getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return t("notifications.justNow");
    if (min < 60) return t("notifications.minutesAgo", { count: min });
    const hr = Math.floor(min / 60);
    if (hr < 24) return t("notifications.hoursAgo", { count: hr });
    const d = Math.floor(hr / 24);
    return t("notifications.daysAgo", { count: d });
    // i18n participation
    void i18n;
  };
}

export const NotificationBell = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [open, setOpen] = useState(false);
  const fmt = useRelativeTime();

  const unread = useMemo(() => items.filter(i => !i.read_at).length, [items]);

  useEffect(() => {
    if (!user) { setItems([]); return; }
    let active = true;
    supabase
      .from("notifications")
      .select("id,type,title,body,link,read_at,created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(20)
      .then(({ data }) => { if (active && data) setItems(data as NotificationRow[]); });

    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        (payload) => {
          setItems((prev) => [payload.new as NotificationRow, ...prev].slice(0, 20));
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const updated = payload.new as NotificationRow;
          setItems((prev) => prev.map(n => n.id === updated.id ? updated : n));
        },
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const oldId = (payload.old as { id?: string })?.id;
          if (oldId) setItems((prev) => prev.filter(n => n.id !== oldId));
        },
      )
      .subscribe();

    return () => { active = false; supabase.removeChannel(channel); };
  }, [user]);

  const markOneRead = async (id: string) => {
    if (!user) return;
    setItems(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
    await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("id", id);
  };

  const markAllRead = async () => {
    if (!user) return;
    const ids = items.filter(i => !i.read_at).map(i => i.id);
    if (ids.length === 0) return;
    setItems(prev => prev.map(n => n.read_at ? n : { ...n, read_at: new Date().toISOString() }));
    await supabase.from("notifications").update({ read_at: new Date().toISOString() }).in("id", ids);
  };

  const deleteOne = async (id: string) => {
    if (!user) return;
    const prev = items;
    setItems(p => p.filter(n => n.id !== id));
    const { error } = await supabase.from("notifications").delete().eq("id", id);
    if (error) setItems(prev);
  };

  const clearAll = async () => {
    if (!user || items.length === 0) return;
    const prev = items;
    setItems([]);
    const { error } = await supabase.from("notifications").delete().eq("user_id", user.id);
    if (error) setItems(prev);
  };

  const onItemClick = async (n: NotificationRow) => {
    setOpen(false);
    if (!n.read_at) {
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
      await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("id", n.id);
    }
    if (n.link) navigate(n.link);
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 text-white hover:bg-white/15 hover:text-white"
          aria-label={t("notifications.title")}
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between gap-2 border-b px-3 py-2 flex-shrink-0">
          <span className="text-sm font-semibold">{t("notifications.title")}</span>
          <div className="flex items-center gap-3">
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-primary hover:underline">
                {t("notifications.markAllRead")}
              </button>
            )}
            {items.length > 0 && (
              <button onClick={clearAll} className="text-xs text-muted-foreground hover:text-destructive hover:underline">
                {t("notifications.clearAll")}
              </button>
            )}
          </div>
        </div>
        <ScrollArea className="flex-1 min-h-0">
          {items.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              {t("notifications.empty")}
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((n) => (
                <li key={n.id} className="relative group">
                  <button
                    onClick={() => onItemClick(n)}
                    className={cn(
                      "w-full text-left px-3 py-2.5 hover:bg-muted/60 transition pr-16",
                      !n.read_at && "bg-primary/5",
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read_at && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{n.title}</p>
                        <p className="line-clamp-2 text-xs text-muted-foreground">{n.body}</p>
                        <p className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                          {fmt(n.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                  <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                    {!n.read_at && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markOneRead(n.id); }}
                        className="h-7 w-7 flex items-center justify-center rounded-full text-muted-foreground hover:bg-primary/10 hover:text-primary transition"
                        title={t("notifications.markAsRead")}
                        aria-label={t("notifications.markAsRead")}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteOne(n.id); }}
                      className="h-7 w-7 flex items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition"
                      title={t("notifications.delete")}
                      aria-label={t("notifications.delete")}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
